/**
 * Utilitaire de gestion des rôles dans l'application
 * Permet de convertir les rôles API vers les rôles UI et vice versa
 */

// Rôles côté API
export type ApiRole =
  | 'responsable_achat'
  | 'validateur'
  | 'acheteur'
  | 'gestionnaire'
  | 'interlocuteur'
  | 'signataire'
  | 'Prestataire_Libre'
  | 'Prestataire_Libre_Admin';

// Rôles côté UI
export type UiRole =
  | 'Acheteur signataire admin'
  | 'Acheteur signataire'
  | 'Acheteur'
  | 'Prestataire signataire admin'
  | 'Prestataire signataire'
  | 'Prestataire'
  | 'Prestataire libre'
  | 'Prestataire libre admin';

// Conversion API -> UI
const apiToUiRoleMap: Record<ApiRole, UiRole> = {
  'responsable_achat': 'Acheteur signataire admin',
  'validateur': 'Acheteur signataire',
  'acheteur': 'Acheteur',
  'gestionnaire': 'Prestataire signataire admin',
  'signataire': 'Prestataire signataire',
  'interlocuteur': 'Prestataire',
  'Prestataire_Libre': 'Prestataire libre',
  'Prestataire_Libre_Admin': 'Prestataire libre admin'
};

// Conversion UI -> API
const uiToApiRoleMap: Record<UiRole, ApiRole> = {
  'Acheteur signataire admin': 'responsable_achat',
  'Acheteur signataire': 'validateur',
  'Acheteur': 'acheteur',
  'Prestataire signataire admin': 'gestionnaire',
  'Prestataire signataire': 'signataire',
  'Prestataire': 'interlocuteur',
  'Prestataire libre': 'Prestataire_Libre',
  'Prestataire libre admin': 'Prestataire_Libre_Admin'
};

/**
 * Hiérarchie de sélection du rôle actif, du plus élevé au plus bas.
 * Un utilisateur peut cumuler plusieurs rôles (ex : interlocuteur + gestionnaire) :
 * la session doit s'ouvrir avec le plus élevé, pas avec le premier attaché en base.
 * Doit rester alignée avec App\Support\RoleHierarchy (core/api).
 */
const ROLE_PRIORITY: ApiRole[] = [
  'responsable_achat',
  'validateur',
  'gestionnaire',
  'signataire',
  'Prestataire_Libre_Admin',
  'acheteur',
  'interlocuteur',
  'Prestataire_Libre',
];

/**
 * Retourne le rôle le plus élevé d'un tableau de rôles API.
 * Retombe sur le premier rôle si aucun rôle métier, chaîne vide si tableau vide.
 * @param roles - Rôles de l'utilisateur (format API, ex: réponse /login)
 */
export const getHighestRole = (roles: string[]): string => {
  const highest = ROLE_PRIORITY.find(role => roles.includes(role));
  return highest ?? roles[0] ?? '';
};

/**
 * Convertit un rôle API en rôle UI
 * @param apiRole - Rôle provenant de l'API
 * @returns Rôle formaté pour l'UI
 */
export const convertApiRoleToUiRole = (apiRole: string): string => {
  return apiToUiRoleMap[apiRole as ApiRole] || apiRole;
};

/**
 * Convertit un rôle UI en rôle API
 * @param uiRole - Rôle de l'interface utilisateur
 * @returns Rôle formaté pour l'API
 */
export const convertUiRoleToApiRole = (uiRole: string): string => {
  return uiToApiRoleMap[uiRole as UiRole] || uiRole;
};

/**
 * Vérifie si l'utilisateur est un acheteur (côté client)
 * @param role - Rôle de l'utilisateur (format API)
 */
export const isClientRole = (role: string) => {
  if (!role) return false;

  // Convertir le rôle en minuscules pour une comparaison insensible à la casse
  const roleLowerCase = role.toLowerCase();

  // Liste des rôles client (tous en minuscules)
  const clientRoles = [
    'acheteur',
    'acheteur admin',
    'acheteur signataire',
    'acheteur signataire admin',
    'validateur',
    'responsable_achat',
    'responsable achat'
  ];

  // Vérifier si le rôle (en minuscules) est dans la liste des rôles client
  return clientRoles.some(clientRole => roleLowerCase.includes(clientRole));
};

/**
 * Vérifie si l'utilisateur est un prestataire (côté fournisseur)
 * @param role - Rôle de l'utilisateur (format API)
 */
export const isPrestataireRole = (role: string) => {
  if (!role) return false;

  // Convertir le rôle en minuscules pour une comparaison insensible à la casse
  const roleLowerCase = role.toLowerCase();

  // Liste des rôles prestataire (tous en minuscules)
  const prestataireRoles = [
    'prestataire',
    'prestataire admin',
    'prestataire signataire',
    'prestataire signataire admin',
    'gestionnaire',
    'interlocuteur'
  ];

  // Vérifier si le rôle (en minuscules) est dans la liste des rôles prestataire
  return prestataireRoles.some(prestataireRole => roleLowerCase.includes(prestataireRole));
};

/**
 * Vérifie si l'utilisateur est un administrateur (responsable_achat, gestionnaire ou PL Admin)
 * @param role - Rôle de l'utilisateur (format API)
 * 
 * FRONT-12: Ajout de Prestataire_Libre_Admin
 */
export const isAdminRole = (role: string): boolean => {
  if (!role) return false;

  // Convertir le rôle en minuscules pour une comparaison insensible à la casse
  const roleLowerCase = role.toLowerCase();

  // Liste combinée des rôles admin (API et UI)
  const adminRoles = [
    'responsable_achat',         // Format API
    'gestionnaire',              // Format API
    'acheteur signataire admin', // Format UI
    'prestataire signataire admin', // Format UI
    'responsable achat',         // Variation possible
    // FRONT-12: Ajout des admins Prestataire Libre
    'prestataire_libre_admin',   // Format API
    'prestataire libre admin'    // Format UI
  ];

  // Vérifier si le rôle correspond à l'un des rôles admin
  return adminRoles.some(adminRole => roleLowerCase.includes(adminRole));
};

/**
 * Vérifie si l'utilisateur est un signataire (admin ou validateur)
 * Inclut les responsables ET les validateurs (pour validation d'opérations)
 * @param role - Rôle de l'utilisateur (format API ou UI)
 */
export const isSignataireRole = (role: string): boolean => {
  if (!role) return false;

  // Convertir le rôle en minuscules pour une comparaison insensible à la casse
  const roleLowerCase = role.toLowerCase();

  // Liste des rôles signataires (admin + validateurs)
  const signataireRoles = [
    // Admins (peuvent gérer ET valider)
    'responsable_achat',         // Format API
    'gestionnaire',              // Format API
    'acheteur signataire admin', // Format UI
    'prestataire signataire admin', // Format UI
    // Validateurs (peuvent valider UNIQUEMENT)
    'validateur',                // Format API
    'signataire',                // Format API
    'acheteur signataire',       // Format UI
    'prestataire signataire'     // Format UI
  ];

  // Vérifier si le rôle correspond à l'un des rôles signataires
  return signataireRoles.some(signataireRole => roleLowerCase.includes(signataireRole));
};

/**
 * Vérifie si l'utilisateur est un prestataire libre
 * Les prestataires libres ont un rôle spécifique pour accéder à leur profil de complétion
 * @param role - Rôle de l'utilisateur (format API ou UI)
 */
export const isPrestataireLibreRole = (role: string): boolean => {
  if (!role) return false;

  // Convertir le rôle en minuscules pour une comparaison insensible à la casse
  const roleLowerCase = role.toLowerCase();

  // Liste des rôles prestataire libre
  const prestataireLibreRoles = [
    'prestataire_libre',          // Format API
    'prestataire_libre_admin',    // Format API admin
    'prestataire libre',          // Format avec espace
    'prestataire libre admin'     // Format UI admin
  ];

  // Vérifier si le rôle correspond à l'un des rôles prestataire libre
  return prestataireLibreRoles.some(plRole => roleLowerCase.includes(plRole));
};

// Role rank map for promotions/demotions
const roleRankMap: Partial<Record<UiRole, number>> = {
  'Acheteur': 1,
  'Acheteur signataire': 2,
  'Acheteur signataire admin': 3,
  'Prestataire': 1,
  'Prestataire signataire admin': 3,
  'Prestataire libre': 1,
  'Prestataire libre admin': 2,
};

/**
 * Returns the list of roles a user can be changed to from their current role.
 * Acheteur context: acheteur ↔ validateur ↔ responsable_achat
 * Gestionnaire context: interlocuteur ↔ gestionnaire
 * PL context: Prestataire_Libre ↔ Prestataire_Libre_Admin
 */
export const getRolePeers = (currentRole: UiRole): UiRole[] => {
  switch (currentRole) {
    case 'Acheteur':
    case 'Acheteur signataire':
    case 'Acheteur signataire admin':
      return ['Acheteur', 'Acheteur signataire', 'Acheteur signataire admin'];
    case 'Prestataire':
    case 'Prestataire signataire admin':
      return ['Prestataire', 'Prestataire signataire admin'];
    case 'Prestataire libre':
    case 'Prestataire libre admin':
      return ['Prestataire libre', 'Prestataire libre admin'];
    default:
      return [];
  }
};

/**
 * Returns true if changing from current to target is a promotion.
 */
export const isRolePromotion = (current: UiRole, target: UiRole): boolean => {
  return (roleRankMap[target] ?? 0) > (roleRankMap[current] ?? 0);
};